import { Link, createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { readMmCheckoutContext, type MmCheckoutStash } from '#bv/lib/mmCheckoutSession'
import { getCryptoLedgerSummary } from '#bv/lib/nowpayments'

export const Route = createFileRoute('/donation/thanks')({
  validateSearch: (raw: Record<string, unknown>): { paymentId?: string } => ({
    paymentId: typeof raw.paymentId === 'string' ? raw.paymentId : undefined,
  }),
  component: DonationThanks,
})

function DonationThanks() {
  const search = Route.useSearch()
  const [mm, setMm] = useState<MmCheckoutStash | null>(null)
  const [crypto, setCrypto] = useState<{
    items: { productId: number; name: string; unitPrice: number; quantity: number }[]
    buyerEmail: string
    totalUsd: number
    internalOrderId: string
  } | null>(null)

  useEffect(() => {
    setMm(readMmCheckoutContext())
  }, [])

  useEffect(() => {
    const paymentId = search.paymentId?.trim() || ''
    if (!paymentId) {
      setCrypto(null)
      return
    }
    void getCryptoLedgerSummary({ data: { paymentId } })
      .then((row) => setCrypto(row))
      .catch(() => setCrypto(null))
  }, [search.paymentId])

  const receiptLine = useMemo(() => {
    const cryptoUsd = crypto?.totalUsd
    if (cryptoUsd != null) return `$${cryptoUsd.toFixed(2)}`
    const mmSummary = mm?.purchaseSummary ?? ''
    const m = mmSummary.match(/\$([0-9]+(?:\.[0-9]{1,2})?)/)
    return m ? `$${m[1]}` : ''
  }, [crypto?.totalUsd, mm?.purchaseSummary])

  return (
    <div className="nv-home">
      <section className="nv-hero">
        <div className="nv-wrap">
          <div className="nv-hero3">
            <div className="nv-hero3-spot" aria-hidden />
            <div className="nv-hero3-frame">
              <div className="nv-grain" aria-hidden />
              <div className="nv-hero3-kicker">Thank you</div>
              <h1 className="nv-hero3-h">
                <span className="nv-hero3-h-line">Donation received.</span>
                <span className="nv-hero3-h-line">We appreciate it.</span>
              </h1>
              <p className="nv-hero3-sub">
                Your support helps keep PulseDigital online and improving.
              </p>

              {receiptLine ? (
                <div className="nv-final-card" style={{ marginTop: 18 }}>
                  <div className="nv-final-k">Receipt</div>
                  <div className="nv-final-h">{receiptLine} USD</div>
                  <div className="nv-final-s">
                    If you need help, contact us and include your checkout reference below.
                  </div>
                  <div className="nv-featured-cta">
                    <Link to="/contact" className="nv-btn nv-btn-primary">
                      Contact
                    </Link>
                    <Link to="/shop" className="nv-btn nv-btn-ghost">
                      Back to Pulse Shop
                    </Link>
                  </div>
                </div>
              ) : null}

              <div className="nv-final-card" style={{ marginTop: 16 }}>
                <div className="nv-final-k">Checkout reference</div>
                <div className="nv-final-s">
                  {search.paymentId?.trim()
                    ? `Crypto invoice ID: ${search.paymentId.trim()}`
                    : mm?.checkoutSessionId
                      ? `Card session ID: ${mm.checkoutSessionId}`
                      : 'If you used card or crypto, your provider reference should appear in your browser history.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

